import DrawerAppBarHOC from "../home/DrawerAppBar";
import {
  Autocomplete,
  Box,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import OrderHistoryItem from "./OrderHistoryItem";
import withAuthentication from "../base/AuthComponent";
import { useEffect } from "react";
import { Dish } from "../menu/menuTypes";
import {
  getOrderList,
  setOnlyTakeAway,
  setOnlyToBePrinted,
  setSearchQuery,
  setSelectedUserId,
} from "./ordersSlice";
import { ORDER_LIST } from "./orderHistoryActions";
import { HttpRequest } from "../base/HttpModels";
import { IOrderListRequest } from "./ordersTypes";
import { isMobile } from "react-device-detect";
import { isOrderTakeAway } from "../utils/orderUtils";
import { User } from "../users/usersTypes";

function OrderHistoryComponentBody() {
  const orders = useAppSelector((state) => state.ordersState.orders);
  const menu = useAppSelector((state) => state.menuState.menu);
  const token = useAppSelector((state) => state.loginState.token);
  const users = useAppSelector((state) => state.userState.users);
  const onlyToBePrinted = useAppSelector(
    (state) => state.ordersState.onlyToBePrinted
  );
  const onlyTakeAway = useAppSelector(
    (state) => state.ordersState.onlyTakeAway
  );
  const searchQuery = useAppSelector(
    (state) => state.ordersState.searchQuery
  );
  const selectedUserId = useAppSelector(
    (state) => state.ordersState.selectedUserId
  );

  const dispatch = useAppDispatch();

  const acceptedUsers = users.filter((u) => u.status === "accepted");

  const parsedOrderId =
    searchQuery && /^\d+$/.test(searchQuery.trim())
      ? Number(searchQuery.trim())
      : undefined;

  useEffect(() => {
    if (token !== undefined && token !== null && token !== "") {
      const timeout = setTimeout(() => {
        const orderListRequest: HttpRequest<IOrderListRequest> = {
          action: ORDER_LIST,
          parameters: {
            user_token: token,
            only_take_away: onlyTakeAway ? true : undefined,
            order_id: parsedOrderId,
            opened_by: selectedUserId,
          },
        };
        dispatch(getOrderList(orderListRequest));
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [dispatch, token, onlyTakeAway, parsedOrderId, selectedUserId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (token !== undefined && token !== null && token !== "") {
        const orderListRequest: HttpRequest<IOrderListRequest> = {
          action: ORDER_LIST,
          parameters: {
            user_token: token,
            only_take_away: onlyTakeAway ? true : undefined,
            order_id: parsedOrderId,
            opened_by: selectedUserId,
          },
        };
        dispatch(getOrderList(orderListRequest));
      }
    }, 30000);
    return () => {
      clearInterval(interval);
    };
  }, [dispatch, token, onlyTakeAway, parsedOrderId, selectedUserId]);

  function findUserNameById(id: number | null) {
    if (id == null) {
      return undefined;
    }
    const user = users.find((user) => user.id === id);
    if (user) {
      return user.name;
    } else {
      return "Utente non trovato";
    }
  }

  function findDishById(dishId: number): Dish | undefined {
    return menu
      .flatMap((category) => category.dishes)
      .find((dish) => dish.id === dishId);
  }

  const filteredOrders = orders
    .filter((item) => !onlyToBePrinted || item.assigned_to === null)
    .filter((item) => !onlyTakeAway || isOrderTakeAway(item, menu))
    .filter((item) => {
      if (!searchQuery || searchQuery.trim() === "") return true;
      const query = searchQuery.trim().toLowerCase();
      const matchId = item.id.toString().includes(query);
      const matchTable = (item.table_name || "").toLowerCase().includes(query);
      return matchId || matchTable;
    });

  return (
    <Box
      sx={{
        width: "100%",
        paddingLeft: isMobile ? "0%" : "25%",
        paddingRight: isMobile ? "0%" : "25%",
        display: "block",
        alignItems: "center",
      }}
    >
      <Box sx={{ my: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Cerca per ID ordine o Tavolo..."
          value={searchQuery}
          onChange={(e) => dispatch(setSearchQuery(e.target.value))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="cancella ricerca"
                  onClick={() => dispatch(setSearchQuery(""))}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
      <Box sx={{ my: 1 }}>
        <Autocomplete
          size="small"
          options={acceptedUsers}
          getOptionLabel={(option: User) => option.name}
          value={acceptedUsers.find((u) => u.id === selectedUserId) ?? null}
          onChange={(_event, newValue: User | null) => {
            dispatch(setSelectedUserId(newValue?.id ?? undefined));
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Filtra per cameriere..." />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={onlyToBePrinted}
              onChange={() => dispatch(setOnlyToBePrinted(!onlyToBePrinted))}
              color="primary"
            />
          }
          label="Solo da stampare"
        />
        <FormControlLabel
          control={
            <Switch
              checked={onlyTakeAway}
              onChange={() => dispatch(setOnlyTakeAway(!onlyTakeAway))}
              color="secondary"
            />
          }
          label="Solo asporto"
        />
      </Box>

      {filteredOrders.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", my: 4 }}>
          Nessun ordine trovato con i filtri correnti.
        </Typography>
      ) : (
        filteredOrders.map((item) => (
          <OrderHistoryItem
            key={item.id}
            id={item.id}
            date={new Date(item.creation_date)}
            table={item.table_name}
            orderStatusId={item.order_status_id}
            price={item.order_lines
              .map((line) => {
                const dish = findDishById(line.dish_id);
                if (dish) {
                  return line.quantity * dish.price;
                } else {
                  return 0;
                }
              })
              .reduce((a, b) => a + b, 0)}
            takenBy={findUserNameById(item.opened_by)}
            paidBy={findUserNameById(item.paid_by)}
            servedBy={findUserNameById(item.assigned_to)}
            closedBy={findUserNameById(item.completed_by)}
            openedById={item.opened_by}
            isTakeAway={isOrderTakeAway(item, menu)}
          />
        ))
      )}
    </Box>
  );
}

function OrderHistoryComponent() {
  return (
    <DrawerAppBarHOC
      title="Lista ordini"
      content={<OrderHistoryComponentBody />}
    />
  );
}

export default withAuthentication(OrderHistoryComponent);
